import { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { formatNumber, formatMs, formatPercentage } from '../utils/format';
import {
  TrendingUp,
  TrendingDown,
  Activity,
  Clock,
  AlertTriangle,
  Globe,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

interface AnalyticsData {
  summary: {
    totalRequests: number;
    requestsChange: number;
    avgResponseTime: number;
    responseTimeChange: number;
    errorRate: number;
    errorRateChange: number;
    uniqueTools: number;
  };
  requestsOverTime: Array<{ date: string; requests: number; errors: number }>;
  responseTimeDistribution: Array<{ range: string; count: number }>;
  toolUsage: Array<{ tool: string; count: number }>;
  errorsByType: Array<{ type: string; count: number }>;
  geographicDistribution: Array<{ region: string; requests: number }>;
}

const COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444'];

export default function Analytics() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [period, setPeriod] = useState<'7d' | '30d' | '90d'>('30d');

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  async function loadAnalytics() {
    try {
      const usage = await api.get<{ data: { requests: { used: number }; success_rate: number } }>('/api/usage');
      setData({
        summary: {
          totalRequests: usage.data.requests.used,
          requestsChange: 0,
          avgResponseTime: 0,
          responseTimeChange: 0,
          errorRate: 100 - usage.data.success_rate,
          errorRateChange: 0,
          uniqueTools: 0,
        },
        requestsOverTime: [],
        responseTimeDistribution: [],
        toolUsage: [],
        errorsByType: [],
        geographicDistribution: [],
      });
    } catch (error) {
      console.error('Failed to load analytics:', error);
      setData({
        summary: {
          totalRequests: 0, requestsChange: 0, avgResponseTime: 0,
          responseTimeChange: 0, errorRate: 0, errorRateChange: 0, uniqueTools: 0,
        },
        requestsOverTime: [], responseTimeDistribution: [],
        toolUsage: [], errorsByType: [], geographicDistribution: [],
      });
    } finally {
      setIsLoading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-n2f-accent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-n2f-text">Analytics</h1>
          <p className="text-n2f-text-secondary">Deep insights into your API usage</p>
        </div>
        <div className="flex items-center bg-n2f-elevated rounded-lg p-1">
          {(['7d', '30d', '90d'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 text-sm rounded-md transition-colors ${
                period === p
                  ? 'bg-n2f-card shadow text-n2f-text'
                  : 'text-n2f-text-muted hover:text-n2f-text'
              }`}
            >
              {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : '90 Days'}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="stat-card">
          <div className="flex items-center justify-between">
            <Activity className="h-8 w-8 text-n2f-accent" />
            <div className={`flex items-center text-sm ${data?.summary.requestsChange && data.summary.requestsChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {data?.summary.requestsChange && data.summary.requestsChange >= 0 ? <TrendingUp className="h-4 w-4 mr-1" /> : <TrendingDown className="h-4 w-4 mr-1" />}
              {formatPercentage(Math.abs(data?.summary.requestsChange || 0))}
            </div>
          </div>
          <div className="stat-value mt-4">{formatNumber(data?.summary.totalRequests || 0)}</div>
          <div className="stat-label">Total Requests</div>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between">
            <Clock className="h-8 w-8 text-blue-400" />
            <div className={`flex items-center text-sm ${data?.summary.responseTimeChange && data.summary.responseTimeChange <= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {data?.summary.responseTimeChange && data.summary.responseTimeChange <= 0 ? <TrendingDown className="h-4 w-4 mr-1" /> : <TrendingUp className="h-4 w-4 mr-1" />}
              {formatPercentage(Math.abs(data?.summary.responseTimeChange || 0))}
            </div>
          </div>
          <div className="stat-value mt-4">{formatMs(data?.summary.avgResponseTime || 0)}</div>
          <div className="stat-label">Avg Response Time</div>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between">
            <AlertTriangle className="h-8 w-8 text-orange-400" />
            <div className={`flex items-center text-sm ${data?.summary.errorRateChange && data.summary.errorRateChange <= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {data?.summary.errorRateChange && data.summary.errorRateChange <= 0 ? <TrendingDown className="h-4 w-4 mr-1" /> : <TrendingUp className="h-4 w-4 mr-1" />}
              {formatPercentage(Math.abs(data?.summary.errorRateChange || 0))}
            </div>
          </div>
          <div className="stat-value mt-4">{formatPercentage(data?.summary.errorRate || 0)}</div>
          <div className="stat-label">Error Rate</div>
        </div>

        <div className="stat-card">
          <Globe className="h-8 w-8 text-green-400" />
          <div className="stat-value mt-4">{data?.summary.uniqueTools || 0}</div>
          <div className="stat-label">Tools Used</div>
        </div>
      </div>

      {/* Requests Over Time */}
      <div className="card">
        <h2 className="text-lg font-semibold text-n2f-text mb-4">Requests Over Time</h2>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data?.requestsOverTime || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
              <XAxis dataKey="date" stroke="#6b7280" fontSize={12} />
              <YAxis stroke="#6b7280" fontSize={12} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#0a0b0e',
                  border: '1px solid #1f2937',
                  borderRadius: '8px',
                  color: '#f9fafb',
                }}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey="requests"
                stroke="#3b82f6"
                fill="#3b82f6"
                fillOpacity={0.2}
                name="Requests"
              />
              <Area
                type="monotone"
                dataKey="errors"
                stroke="#ef4444"
                fill="#ef4444"
                fillOpacity={0.2}
                name="Errors"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Tool Usage */}
        <div className="card">
          <h2 className="text-lg font-semibold text-n2f-text mb-4">Tool Usage</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.toolUsage || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis type="number" stroke="#6b7280" fontSize={12} />
                <YAxis type="category" dataKey="tool" stroke="#6b7280" fontSize={10} width={100} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#0a0b0e',
                    border: '1px solid #1f2937',
                    borderRadius: '8px',
                    color: '#f9fafb',
                  }}
                />
                <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Response Time Distribution */}
        <div className="card">
          <h2 className="text-lg font-semibold text-n2f-text mb-4">Response Time Distribution</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data?.responseTimeDistribution || []}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="count"
                  nameKey="range"
                  label={({ range, percent }) => `${range} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={false}
                >
                  {data?.responseTimeDistribution.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#0a0b0e',
                    border: '1px solid #1f2937',
                    borderRadius: '8px',
                    color: '#f9fafb',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Errors by Type */}
        <div className="card">
          <h2 className="text-lg font-semibold text-n2f-text mb-4">Errors by Type</h2>
          <div className="space-y-3">
            {data?.errorsByType.map((error, index) => (
              <div key={error.type} className="flex items-center">
                <div
                  className="w-3 h-3 rounded-full mr-3"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <span className="flex-1 text-sm text-n2f-text-secondary">{error.type}</span>
                <span className="text-sm font-medium text-n2f-text">{formatNumber(error.count)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Geographic Distribution */}
        <div className="card">
          <h2 className="text-lg font-semibold text-n2f-text mb-4">Geographic Distribution</h2>
          <div className="space-y-3">
            {data?.geographicDistribution.map((region) => (
              <div key={region.region}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-n2f-text-secondary">{region.region}</span>
                  <span className="text-sm font-medium text-n2f-text">{formatNumber(region.requests)}</span>
                </div>
                <div className="h-2 bg-n2f-elevated rounded-full overflow-hidden">
                  <div
                    className="h-full bg-n2f-accent rounded-full"
                    style={{
                      width: `${(region.requests / (data?.geographicDistribution[0]?.requests || 1)) * 100}%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
